import { useFigureCompositionStore, type LegendPosition } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function LegendControls() {
  // Use granular selectors to prevent re-renders when unrelated parts change
  const figureLegend = useFigureCompositionStore((state) => state.currentComposition?.figureLegend);
  const panels = useFigureCompositionStore((state) => state.currentComposition?.panels);
  const legendPosition = useFigureCompositionStore((state) => state.currentComposition?.legendPosition ?? 'auto');
  const setFigureLegend = useFigureCompositionStore((state) => state.setFigureLegend);
  const setLegendPosition = useFigureCompositionStore((state) => state.setLegendPosition);
  const updatePanelOverrides = useFigureCompositionStore((state) => state.updatePanelOverrides);

  if (!panels || panels.length === 0) {
    return null;
  }
  
  // Create currentComposition-like object for compatibility
  const currentComposition = { figureLegend, panels } as any;

  const determineCurrentMode = (): 'per-panel' | 'figure-level' | 'none' => {
    if (!currentComposition || !currentComposition.panels.length) {
      return 'per-panel';
    }

    // Check if figureLegend is explicitly enabled (this is the source of truth for figure-level mode)
    if (currentComposition.figureLegend?.enabled) {
      return 'figure-level';
    }

    const allHidden = currentComposition.panels.every(
      (panel) => panel.layoutOverrides?.showLegend === false
    );

    const anyShown = currentComposition.panels.some(
      (panel) => panel.layoutOverrides?.showLegend === true
    );

    if (allHidden && !anyShown) {
      return 'none';
    }

    return 'per-panel';
  };

  const currentMode = determineCurrentMode();

  const handleLegendModeChange = (mode: 'per-panel' | 'figure-level' | 'none') => {
    const composition = useFigureCompositionStore.getState().currentComposition;
    const { updatePanelOverrides, setFigureLegend, setLegendPosition } = useFigureCompositionStore.getState();

    if (!composition) return;

    const isMultiPanel = composition.panels.length >= 2;

    if (mode === 'per-panel') {
      // When switching to per-panel mode, keep legendPosition as-is but disable figure-level legend
      composition.panels.forEach((panel) => {
        updatePanelOverrides(panel.id, { showLegend: undefined });
      });
      setFigureLegend({ enabled: false, panelId: undefined });
    }

    if (mode === 'figure-level') {
      // When switching to figure-level mode, set legendPosition to 'top_outside' for multi-panel
      if (isMultiPanel) {
        setLegendPosition('top_outside');
      }
      composition.panels.forEach((panel) => {
        updatePanelOverrides(panel.id, { showLegend: false });
      });
      const defaultPanelId = composition.panels[0]?.id;
      if (defaultPanelId) {
        setFigureLegend({ enabled: true, panelId: defaultPanelId });
      }
    }

    if (mode === 'none') {
      // When switching to none mode, set legendPosition to 'none'
      if (isMultiPanel) {
        setLegendPosition('none');
      }
      composition.panels.forEach((panel) => {
        updatePanelOverrides(panel.id, { showLegend: false });
      });
      setFigureLegend({ enabled: false, panelId: undefined });
    }
  };

  const handlePanelIdChange = (panelId: string) => {
    const { currentComposition, setFigureLegend } = useFigureCompositionStore.getState();

    if (!currentComposition) return;

    const exists = currentComposition.panels.some((p) => p.id === panelId);
    if (!exists) {
      console.warn(`LegendControls: panel ${panelId} not found`);
      return;
    }

    setFigureLegend({ panelId });
  };

  const isMultiPanel = panels && panels.length >= 2;
  const effectiveLegendPosition = isMultiPanel && legendPosition === 'auto' ? 'top_outside' : legendPosition;

  return (
    <div className="flex flex-col gap-4">
      {/* Legend Position Control - shown for multi-panel figures */}
      {isMultiPanel && (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Position</Label>
          <Select
            value={legendPosition}
            onValueChange={(value) => {
              const newPosition = value as LegendPosition;
              setLegendPosition(newPosition);
              
              // DO NOT automatically switch legend mode - let the user control it independently
              // The position setting works in both per-panel and figure-level modes
            }}
          >
            <SelectTrigger className="mt-1 h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto {isMultiPanel ? '(Top outside)' : ''}</SelectItem>
              <SelectItem value="top_outside">Top outside</SelectItem>
              <SelectItem value="bottom_outside">Bottom outside</SelectItem>
              <SelectItem value="right_outside">Right outside</SelectItem>
              <SelectItem value="none">None (hide legend)</SelectItem>
            </SelectContent>
          </Select>
          {isMultiPanel && legendPosition === 'auto' && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Auto = Top outside (recommended)
            </p>
          )}
          {isMultiPanel && legendPosition !== 'none' && currentMode === 'per-panel' && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Position applies when "Single figure legend" mode is selected
            </p>
          )}
        </div>
      )}

      {/* Legend Mode Control */}
      <div className="space-y-2">
        <Label className="text-sm text-muted-foreground">Legend mode</Label>
        <Select
          value={currentMode}
          onValueChange={(value) => handleLegendModeChange(value as any)}
        >
          <SelectTrigger className="mt-1 h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="per-panel">Per panel</SelectItem>
            <SelectItem value="figure-level">Single figure legend</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
        {currentMode === 'figure-level' && currentComposition.panels.length > 0 && (
          <div className="space-y-2 mt-3">
            <Label className="text-sm text-muted-foreground">Legend panel</Label>
            <Select
              value={currentComposition.figureLegend?.panelId ?? currentComposition.panels[0]?.id ?? ''}
              onValueChange={(value) => handlePanelIdChange(value)}
            >
              <SelectTrigger className="mt-1 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currentComposition.panels.map((panel) => (
                  <SelectItem key={panel.id} value={panel.id}>
                    {panel.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Panel that displays the figure-level legend</p>
          </div>
        )}
      </div>
    </div>
  );
}
